import { useRef, useState } from 'react';
import { NotebookPen, Plus } from 'lucide-react';

import { createTask } from '@/db/repositories/tasks';
import { CaptureAreaSelect } from '@/modules/areas/CaptureArea';
import { useCaptureArea } from '@/modules/areas/useCaptureArea';
import { cn } from '@/lib/utils/cn';
import type { ID, TaskStatus } from '@/domain/types';

interface QuickCaptureProps {
  placeholder?: string;
  status?: TaskStatus;
  plannedDate?: string;
  /** Fester Bereich, z. B. auf dem Bereichs-Board (F-01) - dann ohne Auswahl. */
  festerBereich?: ID;
}

/**
 * Schnellerfassung nach GTD: ein Feld, sonst nichts.
 *
 * Der Gedanke dahinter ist, dass Erfassen und Einordnen getrennte Vorgaenge
 * sind. Wer beim Notieren schon ueber Bereich, Faelligkeit und Prioritaet
 * nachdenken muss, notiert seltener.
 *
 * Eine Notiz ist optional und zugeklappt (F-05) - wer sie braucht, klappt
 * sie auf, alle anderen tippen weiter nur den Titel.
 *
 * Bei aktivem Bereichsfilter erbt die neue Aufgabe einen gefilterten Bereich -
 * sonst waere sie gespeichert, aber sofort ausgeblendet (Roadmap T-01).
 */
export function QuickCapture({
  placeholder = 'Was liegt an?', status = 'inbox', plannedDate, festerBereich,
}: QuickCaptureProps) {
  const [value, setValue] = useState('');
  const [notiz, setNotiz] = useState('');
  const [mitNotiz, setMitNotiz] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { areaId, setAreaId, choices } = useCaptureArea();

  async function submit() {
    const title = value.trim();
    if (!title) return;

    const notes = notiz.trim() || undefined;
    setValue('');
    setNotiz('');
    await createTask({ title, status, areaId: festerBereich ?? areaId, plannedDate, notes });
    inputRef.current?.focus();
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
            if (e.key === 'Escape') setValue('');
          }}
          className={cn(
            'h-10 w-full rounded-lg border border-line bg-surface px-3.5 text-sm text-ink',
            'placeholder:text-ink-faint focus:border-accent focus:outline-none',
          )}
        />
        {!festerBereich && <CaptureAreaSelect value={areaId} choices={choices} onChange={setAreaId} />}
        <button
          type="button"
          onClick={() => setMitNotiz((x) => !x)}
          aria-pressed={mitNotiz}
          aria-label="Notiz hinzufügen"
          title="Notiz hinzufügen"
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-lg border transition-colors',
            mitNotiz ? 'border-accent text-accent-text' : 'border-line text-ink-faint hover:text-ink',
          )}
        >
          <NotebookPen className="size-4" />
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!value.trim()}
          aria-label="Hinzufügen"
          className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent text-on-accent transition-opacity disabled:opacity-40"
        >
          <Plus className="size-4" />
        </button>
      </div>
      {mitNotiz && (
        <textarea
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void submit(); }}
          rows={3}
          aria-label="Notiz zur neuen Aufgabe"
          placeholder="Notiz (optional) – Strg + Enter legt die Aufgabe an"
          className="w-full resize-y rounded-lg border border-line bg-surface px-3.5 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none"
        />
      )}
    </div>
  );
}
